package app.nestbridge.feature.invitations.service

import app.nestbridge.domain.identity.api.model.ProfessionalServiceTypeDto
import app.nestbridge.domain.identity.model.ProfessionalServiceType
import app.nestbridge.feature.careteam.api.model.TeamRelationshipRoleDto
import app.nestbridge.feature.careteam.model.TeamRelationshipRole
import app.nestbridge.feature.invitations.api.model.InvitationDeliveryMethodDto
import app.nestbridge.feature.invitations.api.model.InvitationDto
import app.nestbridge.feature.invitations.api.model.InvitationStatusDto
import app.nestbridge.feature.invitations.api.model.OrganizationTypeDto
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.model.InvitationStatus
import app.nestbridge.feature.invitations.model.OrganizationType

internal fun InvitationDto.toDomain(): Invitation = Invitation(
    id = id,
    status = status.toDomain(),
    inviterUserProfileId = inviterUserProfileId,
    inviterDisplayName = inviterDisplayName,
    inviterOrganizationName = inviterOrganizationName,
    inviterOrganizationType = inviterOrganizationType?.toDomain(),
    childId = childId,
    childDisplayName = childDisplayName,
    teamId = teamId,
    teamType = teamType?.toDomainProfessional(),
    proposedTeamRole = proposedTeamRole.toDomainRole(),
    grantsChildStewardship = grantsChildStewardship,
    deliveryMethod = deliveryMethod.toDomain(),
    deliveryDestination = deliveryDestination,
    createdAt = createdAt,
    createdBy = createdBy,
    modifiedAt = modifiedAt,
    modifiedBy = modifiedBy,
)

internal fun Invitation.toDto(): InvitationDto = InvitationDto(
    id = id,
    status = status.toDto(),
    inviterUserProfileId = inviterUserProfileId,
    inviterDisplayName = inviterDisplayName,
    inviterOrganizationName = inviterOrganizationName,
    inviterOrganizationType = inviterOrganizationType?.toDto(),
    childId = childId,
    childDisplayName = childDisplayName,
    teamId = teamId,
    teamType = teamType?.toDtoProfessional(),
    proposedTeamRole = proposedTeamRole.toDtoRole(),
    grantsChildStewardship = grantsChildStewardship,
    deliveryMethod = deliveryMethod.toDto(),
    deliveryDestination = deliveryDestination,
    createdAt = createdAt,
    createdBy = createdBy,
    modifiedAt = modifiedAt,
    modifiedBy = modifiedBy,
)

internal fun InvitationStatusDto.toDomain(): InvitationStatus = when (this) {
    InvitationStatusDto.PENDING -> InvitationStatus.PENDING
    InvitationStatusDto.ACCEPTED -> InvitationStatus.ACCEPTED
    InvitationStatusDto.DECLINED -> InvitationStatus.DECLINED
    InvitationStatusDto.EXPIRED -> InvitationStatus.EXPIRED
}

internal fun InvitationStatus.toDto(): InvitationStatusDto = when (this) {
    InvitationStatus.PENDING -> InvitationStatusDto.PENDING
    InvitationStatus.ACCEPTED -> InvitationStatusDto.ACCEPTED
    InvitationStatus.DECLINED -> InvitationStatusDto.DECLINED
    InvitationStatus.EXPIRED -> InvitationStatusDto.EXPIRED
}

internal fun InvitationDeliveryMethodDto.toDomain(): InvitationDeliveryMethod = when (this) {
    InvitationDeliveryMethodDto.EMAIL -> InvitationDeliveryMethod.EMAIL
    InvitationDeliveryMethodDto.PHONE -> InvitationDeliveryMethod.PHONE
}

internal fun InvitationDeliveryMethod.toDto(): InvitationDeliveryMethodDto = when (this) {
    InvitationDeliveryMethod.EMAIL -> InvitationDeliveryMethodDto.EMAIL
    InvitationDeliveryMethod.PHONE -> InvitationDeliveryMethodDto.PHONE
}

internal fun OrganizationTypeDto.toDomain(): OrganizationType = when (this) {
    OrganizationTypeDto.EARLY_INTERVENTION -> OrganizationType.EARLY_INTERVENTION
    OrganizationTypeDto.PRIVATE_PRACTICE -> OrganizationType.PRIVATE_PRACTICE
    OrganizationTypeDto.SCHOOL_BASED -> OrganizationType.SCHOOL_BASED
    OrganizationTypeDto.DAYCARE -> OrganizationType.DAYCARE
    OrganizationTypeDto.ABA_CENTER -> OrganizationType.ABA_CENTER
    OrganizationTypeDto.OTHER -> OrganizationType.OTHER
}

internal fun OrganizationType.toDto(): OrganizationTypeDto = when (this) {
    OrganizationType.EARLY_INTERVENTION -> OrganizationTypeDto.EARLY_INTERVENTION
    OrganizationType.PRIVATE_PRACTICE -> OrganizationTypeDto.PRIVATE_PRACTICE
    OrganizationType.SCHOOL_BASED -> OrganizationTypeDto.SCHOOL_BASED
    OrganizationType.DAYCARE -> OrganizationTypeDto.DAYCARE
    OrganizationType.ABA_CENTER -> OrganizationTypeDto.ABA_CENTER
    OrganizationType.OTHER -> OrganizationTypeDto.OTHER
}

private fun ProfessionalServiceTypeDto.toDomainProfessional(): ProfessionalServiceType = when (this) {
    ProfessionalServiceTypeDto.SpeechLanguageTherapy -> ProfessionalServiceType.SpeechLanguageTherapy
    ProfessionalServiceTypeDto.OccupationalTherapy -> ProfessionalServiceType.OccupationalTherapy
    ProfessionalServiceTypeDto.PhysicalTherapy -> ProfessionalServiceType.PhysicalTherapy
    ProfessionalServiceTypeDto.DevelopmentalTherapy -> ProfessionalServiceType.DevelopmentalTherapy
    ProfessionalServiceTypeDto.AppliedBehaviorAnalysis -> ProfessionalServiceType.AppliedBehaviorAnalysis
    ProfessionalServiceTypeDto.FeedingTherapy -> ProfessionalServiceType.FeedingTherapy
    ProfessionalServiceTypeDto.MentalHealthCounseling -> ProfessionalServiceType.MentalHealthCounseling
    ProfessionalServiceTypeDto.Psychology -> ProfessionalServiceType.Psychology
    ProfessionalServiceTypeDto.SocialWork -> ProfessionalServiceType.SocialWork
    ProfessionalServiceTypeDto.EarlyChildhoodEducation -> ProfessionalServiceType.EarlyChildhoodEducation
    ProfessionalServiceTypeDto.VisionServices -> ProfessionalServiceType.VisionServices
    ProfessionalServiceTypeDto.HearingAudiology -> ProfessionalServiceType.HearingAudiology
    ProfessionalServiceTypeDto.Other -> ProfessionalServiceType.Other
}

private fun ProfessionalServiceType.toDtoProfessional(): ProfessionalServiceTypeDto = when (this) {
    ProfessionalServiceType.SpeechLanguageTherapy -> ProfessionalServiceTypeDto.SpeechLanguageTherapy
    ProfessionalServiceType.OccupationalTherapy -> ProfessionalServiceTypeDto.OccupationalTherapy
    ProfessionalServiceType.PhysicalTherapy -> ProfessionalServiceTypeDto.PhysicalTherapy
    ProfessionalServiceType.DevelopmentalTherapy -> ProfessionalServiceTypeDto.DevelopmentalTherapy
    ProfessionalServiceType.AppliedBehaviorAnalysis -> ProfessionalServiceTypeDto.AppliedBehaviorAnalysis
    ProfessionalServiceType.FeedingTherapy -> ProfessionalServiceTypeDto.FeedingTherapy
    ProfessionalServiceType.MentalHealthCounseling -> ProfessionalServiceTypeDto.MentalHealthCounseling
    ProfessionalServiceType.Psychology -> ProfessionalServiceTypeDto.Psychology
    ProfessionalServiceType.SocialWork -> ProfessionalServiceTypeDto.SocialWork
    ProfessionalServiceType.EarlyChildhoodEducation -> ProfessionalServiceTypeDto.EarlyChildhoodEducation
    ProfessionalServiceType.VisionServices -> ProfessionalServiceTypeDto.VisionServices
    ProfessionalServiceType.HearingAudiology -> ProfessionalServiceTypeDto.HearingAudiology
    ProfessionalServiceType.Other -> ProfessionalServiceTypeDto.Other
}

private fun TeamRelationshipRoleDto.toDomainRole(): TeamRelationshipRole = when (this) {
    TeamRelationshipRoleDto.ORGANIZER -> TeamRelationshipRole.ORGANIZER
    TeamRelationshipRoleDto.THERAPIST -> TeamRelationshipRole.THERAPIST
    TeamRelationshipRoleDto.PARENT -> TeamRelationshipRole.PARENT
    TeamRelationshipRoleDto.OBSERVER -> TeamRelationshipRole.OBSERVER
}

private fun TeamRelationshipRole.toDtoRole(): TeamRelationshipRoleDto = when (this) {
    TeamRelationshipRole.ORGANIZER -> TeamRelationshipRoleDto.ORGANIZER
    TeamRelationshipRole.THERAPIST -> TeamRelationshipRoleDto.THERAPIST
    TeamRelationshipRole.PARENT -> TeamRelationshipRoleDto.PARENT
    TeamRelationshipRole.OBSERVER -> TeamRelationshipRoleDto.OBSERVER
}
